/**
 * RD-ROLE-006 — the seen-stamp + role-gate decision layer behind the Office
 * Inbox auto-open gate. Mirrors `estimateResponseSummary.js` (same per-user
 * localStorage read-receipt pattern), simplified for a much narrower job:
 * `office_tasks` is a bounded set of currently-OPEN work items, not a
 * historical feed, so there is no first-visit/max lookback clamp to get
 * right — "never seen" simply means every open task counts as unseen.
 *
 * Everything here is PURE (no supabase, no window except behind try/catch) so
 * the decision logic is unit-testable without a database or a browser.
 *
 * RD-NOTICES-V2: `writeOfficeInboxSeenAt` also write-throughs to the server
 * as notice_key `office_inbox_seen` (acked_at) — this store already takes
 * `tenantId` explicitly, so no session lookup is needed. `opts.skipSync`
 * (merge-from-server path) and `mergeOfficeInboxSeenFromServer` /
 * `collectOfficeInboxReceiptsForUpload` follow the same shape as every other
 * store in this feature.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';

export const OFFICE_INBOX_NOTICE_KEY = 'office_inbox_seen';

/**
 * Roles that may see the Office Inbox at all — badge, card, gate, modal.
 * Supervisor/worker/operations/platform get nothing new (RD-ROLE-006 §1.6):
 * ops shares AdminHome and is money-blind (an unpriced-extra task carries no
 * dollar figure, but the modal it opens leads straight to setting one), so the
 * exclusion is a role check here, not a mount-point assumption.
 */
export const OFFICE_INBOX_ROLES = Object.freeze(['admin', 'billing']);

export function canSeeOfficeInbox(role) {
  return OFFICE_INBOX_ROLES.includes(String(role || ''));
}

/** localStorage keys are per USER *and* per TENANT — a shared office device
 *  must not cross-mark a second tenant's inbox as seen (final review minor). */
export function officeInboxSeenKey(userId, tenantId) {
  return `cvx-office-inbox-seen-v1:${tenantId || 'tenant'}:${userId || 'anon'}`;
}

/**
 * Fail-soft in both directions (private-mode `getItem` throws; a corrupted
 * value must not crash a dashboard): unreadable → "never looked" (null).
 */
export function readOfficeInboxSeenAt(userId, tenantId, storage) {
  try {
    const raw = (storage || window.localStorage).getItem(officeInboxSeenKey(userId, tenantId));
    if (!raw) return null;
    return Number.isNaN(Date.parse(raw)) ? null : raw;
  } catch {
    return null;
  }
}

/** MONOTONIC (audit 2026-09-26, class B): writes max(current, new). A
 *  dismiss on a device whose clock lags one that already synced a later
 *  stamp must not move the watermark back and re-greet tasks in between. */
export function writeOfficeInboxSeenAt(userId, tenantId, isoInstant, storage, opts = {}) {
  try {
    const next = maxIso(readOfficeInboxSeenAt(userId, tenantId, storage), isoInstant);
    if (!next) return false;
    (storage || window.localStorage).setItem(officeInboxSeenKey(userId, tenantId), next);
    if (!opts.skipSync && userId && tenantId) {
      upsertNoticeReceipts({
        tenantId, userId, rows: [{ notice_key: OFFICE_INBOX_NOTICE_KEY, acked_at: next }],
      });
    }
    return true;
  } catch {
    return false;
  }
}

/** Server row → local store, MONOTONIC (never move the seen stamp back). */
export function mergeOfficeInboxSeenFromServer(userId, tenantId, ackedAt, storage) {
  if (!userId || !tenantId || !ackedAt) return;
  const current = readOfficeInboxSeenAt(userId, tenantId, storage);
  if (isNewerIso(ackedAt, current)) {
    writeOfficeInboxSeenAt(userId, tenantId, ackedAt, storage, { skipSync: true });
  }
}

/** Snapshot for the one-time post-hydration upload. */
export function collectOfficeInboxReceiptsForUpload(userId, tenantId, storage) {
  const seenAt = readOfficeInboxSeenAt(userId, tenantId, storage);
  return seenAt ? [{ notice_key: OFFICE_INBOX_NOTICE_KEY, acked_at: seenAt }] : [];
}

/**
 * Which of `rows` (each carrying `created_at`) are unseen — strictly newer
 * than `lastSeenIso`. No stamp (`null`/unparseable) means every row currently
 * open is unseen: unlike the estimate-response feed, this set is already
 * bounded to OPEN tasks, so there is no "wall of history" risk to clamp
 * against. A row whose `created_at` is unparseable is dropped rather than
 * shown — we cannot honestly say when it happened.
 */
export function selectUnseenOfficeTasks(rows, lastSeenIso) {
  const sinceMs = lastSeenIso ? Date.parse(lastSeenIso) : NaN;
  const since = Number.isNaN(sinceMs) ? -Infinity : sinceMs;
  return (rows || []).filter((row) => {
    const at = Date.parse(row?.created_at || row?.createdAt || '');
    return !Number.isNaN(at) && at > since;
  });
}

export function hasUnseenOfficeTasks(rows, lastSeenIso) {
  return selectUnseenOfficeTasks(rows, lastSeenIso).length > 0;
}
